import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Info, Plus } from 'lucide-react';
import { resolveReferralCodeListStatus, type PanelReferralCodeDto } from '@icebreaker/shared';
import { Button, EmptyState, Skeleton, Table, Tag, TruncatedText } from '@icebreaker/ui';
import { referralCodeDetailPath } from '@/app/routes';
import adminStyles from '@/features/admin/ui/admin.module.css';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { REFERRAL_STATUS_VARIANT, usageText, validityText } from '../model/referral-code-view';
import {
  fetchReferralCodes,
  referralNoticeDismissed,
  referralTabChanged,
  type ReferralTab,
} from '../store/referral-codes.slice';
import { ReferralCodeDialog } from './referral-code-dialog';
import { ReferralCodeDrawer } from './referral-code-drawer';
import styles from './referral-codes.module.css';

/** Kullanım detayının adres parametresi (`?kod=<kimlik>`): bağlantı paylaşılabilir. */
export const REFERRAL_DETAIL_PARAM = 'kod';

/** Bildirimin ekranda kalma süresi. Satır vurgusu kalır, yalnız bildirim kapanır. */
const NOTICE_MS = 6000;

type DialogState = { readonly mode: 'create' } | { readonly mode: 'edit'; readonly id: string };

/**
 * REFERANS KODLARI LİSTESİ (P7).
 *
 * "Kod oluştur" olumlu ana eylemdir, başlıkta da boş durumda da dolu (düğme
 * standardı 19.10.2026). Sekmeler Aktif anahtarına göre
 * sayar (Tümü / Aktif / Pasif); durum etiketi tarih ve tavanla birlikte
 * `resolveReferralCodeListStatus`ten gelir, öncelik bileşende yazılmaz.
 * Satıra tıklayınca kullanım detayı SAĞDAN açılır ve adres `?kod=` taşır.
 * Başarılı oluşturmadan sonra diyalog kapanır, yeni satır en üstte kiremit
 * vurguyla ve bildirimle görünür.
 */
export function ReferralCodesScreen() {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const state = useAppSelector((root: RootState) => root.panelReferral);
  const { list, tab, highlightId, notice } = state;
  const [params, setParams] = useSearchParams();
  const openId = params.get(REFERRAL_DETAIL_PARAM);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  // Durum etiketinin "bugün"ü: her çizimde şimdiki an (gün dönümünde etiket kendiliğinden döner).
  const now = new Date(Date.now());

  const refresh = useCallback(() => {
    dispatch(fetchReferralCodes.request());
  }, [dispatch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    window.addEventListener('online', refresh);
    return () => window.removeEventListener('online', refresh);
  }, [refresh]);

  useEffect(() => {
    if (notice === null) return undefined;
    const handle = window.setTimeout(() => dispatch(referralNoticeDismissed()), NOTICE_MS);
    return () => window.clearTimeout(handle);
  }, [dispatch, notice]);

  const closeDetail = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete(REFERRAL_DETAIL_PARAM);
    setParams(next, { replace: true });
  }, [params, setParams]);

  const counts = {
    all: list.items.length,
    active: list.items.filter((item) => item.active).length,
    inactive: list.items.filter((item) => !item.active).length,
  };
  const rows = list.items.filter((item) =>
    tab === 'all' ? true : tab === 'active' ? item.active : !item.active,
  );
  const firstLoad = !list.loaded && list.phase !== 'failed';
  const tabs: readonly { value: ReferralTab; label: string }[] = [
    { value: 'all', label: t.t('admin.referralCodes.tabs.all') },
    { value: 'active', label: t.t('admin.referralCodes.tabs.active') },
    { value: 'inactive', label: t.t('admin.referralCodes.tabs.inactive') },
  ];
  const editing =
    dialog?.mode === 'edit' ? (list.items.find((item) => item.id === dialog.id) ?? null) : null;

  return (
    <section className={adminStyles.screen}>
      <div className={styles.titleRow}>
        <h1 className={adminStyles.title}>{t.t('admin.referralCodes.title')}</h1>
        <Button
          variant="primary"
          icon={<Plus size={16} aria-hidden="true" />}
          onClick={() => setDialog({ mode: 'create' })}
        >
          {t.t('admin.referralCodes.create')}
        </Button>
      </div>
      <p className={styles.lead}>{t.t('admin.referralCodes.lead')}</p>

      {notice !== null ? (
        <p className={styles.notice} role="status">
          <span className={styles.noticeMark} aria-hidden="true">
            <Check size={12} />
          </span>
          <span>
            <b className={styles.mono}>{notice}</b> {t.t('admin.referralCodes.created')}
          </span>
        </p>
      ) : null}

      {list.loaded && list.items.length > 0 ? (
        <div className={styles.tabs} role="group" aria-label={t.t('admin.referralCodes.tabsLabel')}>
          {tabs.map((item) => (
            <button
              key={item.value}
              type="button"
              aria-pressed={tab === item.value}
              className={[styles.tab, tab === item.value ? styles.tabActive : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => dispatch(referralTabChanged(item.value))}
            >
              {item.label}
              <span className={styles.tabCount}>{t.formatNumber(counts[item.value])}</span>
            </button>
          ))}
        </div>
      ) : null}

      {list.phase === 'failed' && !list.loaded ? (
        <div className={styles.errorCard} role="alert">
          <p className={styles.errorBanner}>{t.t('admin.referralCodes.listError')}</p>
          <Button variant="secondary" size="sm" onClick={refresh}>
            {t.t('admin.referralCodes.retry')}
          </Button>
        </div>
      ) : list.loaded && list.items.length === 0 ? (
        <div className={styles.stateBox}>
          <EmptyState
            title={t.t('admin.referralCodes.emptyTitle')}
            body={t.t('admin.referralCodes.emptyBody')}
            preview={<EmptyPreview />}
            previewPlacement="top"
            action={
              <Button variant="primary" onClick={() => setDialog({ mode: 'create' })}>
                {t.t('admin.referralCodes.create')}
              </Button>
            }
          />
        </div>
      ) : list.loaded && rows.length === 0 ? (
        <div className={styles.stateBox}>
          <EmptyState
            title={t.t('admin.referralCodes.filterEmptyTitle')}
            body={t.t('admin.referralCodes.filterEmptyBody')}
            action={
              <Button variant="secondary" onClick={() => dispatch(referralTabChanged('all'))}>
                {t.t('admin.referralCodes.showAll')}
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <div className={styles.tableArea} aria-busy={firstLoad || undefined}>
            <Table aria-label={t.t('admin.referralCodes.title')}>
              <thead>
                <tr>
                  <th className={styles.colCode}>{t.t('admin.referralCodes.table.code')}</th>
                  <th className={styles.colGift}>{t.t('admin.referralCodes.table.gift')}</th>
                  <th>{t.t('admin.referralCodes.table.valid')}</th>
                  <th className={styles.colUsage}>{t.t('admin.referralCodes.table.usage')}</th>
                  <th className={styles.colStatus}>{t.t('admin.referralCodes.table.status')}</th>
                  <th className={styles.colAction}>
                    <span className={styles.srOnly}>
                      {t.t('admin.referralCodes.table.actions')}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {firstLoad
                  ? SKELETON_WIDTHS.map((width, index) => (
                      <tr key={width}>
                        <td>
                          {index === 0 ? (
                            <Skeleton
                              label={t.t('admin.referralCodes.loading')}
                              lines={1}
                              lineHeight={10}
                              widths={[width]}
                            />
                          ) : (
                            <Skeleton decorative lines={1} lineHeight={10} widths={[width]} />
                          )}
                        </td>
                        <td>
                          <Skeleton decorative lines={1} lineHeight={10} widths={['50%']} />
                        </td>
                        <td>
                          <Skeleton decorative lines={1} lineHeight={10} widths={['70%']} />
                        </td>
                        <td>
                          <Skeleton decorative lines={1} lineHeight={10} widths={['60%']} />
                        </td>
                        <td>
                          <Skeleton decorative lines={1} lineHeight={18} widths={['70px']} />
                        </td>
                        <td />
                      </tr>
                    ))
                  : rows.map((row) => (
                      <CodeRow
                        key={row.id}
                        row={row}
                        now={now}
                        selected={row.id === openId}
                        highlighted={row.id === highlightId}
                        onEdit={() => setDialog({ mode: 'edit', id: row.id })}
                      />
                    ))}
              </tbody>
            </Table>
          </div>
          {firstLoad ? null : (
            <p className={styles.rowHint}>
              <Info size={15} aria-hidden="true" className={styles.hintIcon} />
              {t.t('admin.referralCodes.rowHint')}
            </p>
          )}
        </>
      )}

      {openId ? (
        <ReferralCodeDrawer
          id={openId}
          now={now}
          editing={dialog !== null}
          onClose={closeDetail}
          onEdit={(code) => setDialog({ mode: 'edit', id: code.id })}
        />
      ) : null}
      {dialog?.mode === 'create' ? (
        <ReferralCodeDialog mode="create" onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.mode === 'edit' && editing ? (
        <ReferralCodeDialog mode="edit" code={editing} onClose={() => setDialog(null)} />
      ) : null}
    </section>
  );
}

/** İskelet satırlarının ilk sütun genişlikleri (P7 `skelRows`). */
const SKELETON_WIDTHS = ['42%', '30%', '50%', '36%', '46%', '28%'] as const;

function CodeRow({
  row,
  now,
  selected,
  highlighted,
  onEdit,
}: {
  row: PanelReferralCodeDto;
  now: Date;
  selected: boolean;
  highlighted: boolean;
  onEdit: () => void;
}) {
  const t = useTranslation();
  const status = resolveReferralCodeListStatus(row, now);
  return (
    <tr
      className={[
        styles.clickableRow,
        highlighted ? styles.rowHighlight : selected ? styles.rowSelected : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-highlighted={highlighted || undefined}
    >
      {/* 32 karakterlik kod dar sütunda kesilir: tamamı ipucuyla okunur (kesik-metin kapısı). */}
      <TruncatedText as="td" className={styles.codeCell}>
        <Link className={styles.rowLink} to={referralCodeDetailPath(row.id)}>
          {row.name}
        </Link>
      </TruncatedText>
      <td className={styles.giftCell}>
        {t.t('admin.referralCodes.gift', { count: row.giftCredits })}
      </td>
      <td className={styles.validCell}>{validityText(t, row)}</td>
      <td className={styles.usageCell}>{usageText(t, row)}</td>
      <td>
        <Tag variant={REFERRAL_STATUS_VARIANT[status]}>
          {t.t(`admin.referralCodes.statuses.${status}`)}
        </Tag>
      </td>
      <td className={styles.actionCell}>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t.t('admin.referralCodes.editCode', { code: row.name })}
          onClick={onEdit}
        >
          {t.t('admin.referralCodes.edit')}
        </Button>
      </td>
    </tr>
  );
}

/** Örnek kaydın kodu (referansın boş durum kartı); aria-hidden önizlemede durur. */
const PREVIEW_CODE = 'originn100';

/** Boş durumun soluk, kesik çizgili örnek kaydı: ürünün kendi satırı (README 4.7). */
function EmptyPreview() {
  const t = useTranslation();
  return (
    <>
      <div className={styles.previewRow}>
        <span className={styles.previewCode}>{PREVIEW_CODE}</span>
        <span className={styles.previewGift}>{t.formatNumber(1, { signDisplay: 'always' })}</span>
        <span className={styles.previewBar} />
        <span className={styles.previewPill} />
      </div>
      <div className={styles.previewRow}>
        <span className={styles.previewBar} style={{ width: '70%' }} />
        <span className={styles.previewBar} />
        <span className={styles.previewBar} />
        <span className={[styles.previewPill, styles.previewPillMuted].join(' ')} />
      </div>
    </>
  );
}
